import { where, documentId, serverTimestamp } from 'firebase/firestore';
import {
  updateDataById,
  fetchByTable,
  addDataToTable,
  deleteDocById,
} from '../utils';

// Token transactions — immutable log of every credit balance change.
// type: 'topup' (approved request) | 'admin_add' (direct admin load)
//       | 'match_payment' (negative) | 'refund' (reverted match payment)
export const addTokenTransaction = async ({
  userId,
  email,
  amount,
  type,
  shiftId = null,
  requestId = null,
  reference = null,
  createdBy = null,
}) =>
  addDataToTable({
    tableName: 'tokenTransactions',
    data: {
      userId,
      email,
      amount,
      type,
      shiftId,
      requestId,
      reference,
      createdBy,
      createdAt: serverTimestamp(),
    },
  });

// Pending top-up requests are merged into the movements list in the profile
export const fetchPendingTokenRequestsByUser = async userId =>
  fetchByTable({
    tableName: 'tokenRequests',
    whereCondition: where('userId', '==', userId),
  }).then(rows => rows.filter(r => r.status === 'pending'));

// No orderBy in the query (avoids a composite index); sorted client-side.
export const fetchTokenTransactionsByUser = async (userId, setLoading) =>
  fetchByTable({
    tableName: 'tokenTransactions',
    whereCondition: where('userId', '==', userId),
    setLoading,
  }).then(rows =>
    rows.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0)),
  );

export const updateUser = async (id, data, setLoading) =>
  updateDataById({ id, tableName: 'users', data, setLoading });

export const removeFriend = async (userId, friends, friendId, setLoading) =>
  updateDataById({
    id: userId,
    tableName: 'users',
    data: { friendsIds: friends.filter(f => f !== friendId) },
    setLoading,
  });

export const removePlayerFromfriendsList = async (
  userId,
  friendsListId,
  friendsList,
  friendsListPlayerId,
  setLoading,
) => {
  updateDataById({
    id: userId,
    tableName: 'users',
    data: {
      friendsList: friendsList.map(t =>
        t.friendsListName === friendsListId
          ? {
              ...t,
              players: t.players.filter(p => p.id !== friendsListPlayerId),
            }
          : t,
      ),
    },
    setLoading,
  });
};
export const addPlayerToFriendsList = async (
  userId,
  friendsListId,
  friendsList,
  player,
  setLoading,
) =>
  updateDataById({
    id: userId,
    tableName: 'users',
    data: {
      friendsList: friendsList.map(t =>
        t.friendsListName === friendsListId
          ? { ...t, players: [...t.players, player] }
          : t,
      ),
    },
    setLoading,
  });

// Adds multiple players at once in a single Firestore write
export const addPlayersToFriendsList = async (
  userId,
  friendsListId,
  friendsList,
  players,
  setLoading,
) =>
  updateDataById({
    id: userId,
    tableName: 'users',
    data: {
      friendsList: friendsList.map(t =>
        t.friendsListName === friendsListId
          ? { ...t, players: [...t.players, ...players] }
          : t,
      ),
    },
    setLoading,
  });

export const addFriendsListToPlayer = async (
  userId,
  friendsListName,
  setLoading,
  friendsList = [],
) =>
  updateDataById({
    id: userId,
    tableName: 'users',
    data: {
      friendsList: [...friendsList, { friendsListName, players: [] }],
    },
    setLoading,
  });
export const removeFriendsListFromUser = async (
  userId,
  friendsListName,
  setLoading,
  friendsList = [],
) =>
  updateDataById({
    id: userId,
    tableName: 'users',
    data: {
      friendsList: friendsList.filter(
        t => t.friendsListName !== friendsListName,
      ),
    },
    setLoading,
  });

export const renameFriendsList = async (
  userId,
  oldName,
  newName,
  setLoading,
  friendsList = [],
) =>
  updateDataById({
    id: userId,
    tableName: 'users',
    data: {
      friendsList: friendsList.map(t =>
        t.friendsListName === oldName ? { ...t, friendsListName: newName } : t,
      ),
    },
    setLoading,
  });

export const fetchUserByEmail = async (email, setLoading) =>
  fetchByTable({
    tableName: 'users',
    whereCondition: where('email', '==', email),
    setLoading,
  });

export const fetchUserById = async (id, setLoading) =>
  fetchByTable({
    tableName: 'users',
    whereCondition: where(documentId(), '==', id),
    setLoading,
  });

// Firestore 'in' queries cap at 30 comparison values. Chunk the ids so
// arbitrarily large invite/roster lists still resolve to a full user map
// (previously threw "IN supports up to 30 comparison values" on big shifts).
export const fetchUsersById = async (ids, setLoading) => {
  const unique = Array.from(new Set((ids ?? []).filter(Boolean)));
  if (unique.length === 0) return [];
  setLoading?.(true);
  try {
    const chunks = [];
    for (let i = 0; i < unique.length; i += 30) chunks.push(unique.slice(i, i + 30));
    const results = await Promise.all(chunks.map(chunk => fetchByTable({
      tableName: 'users',
      whereCondition: where(documentId(), 'in', chunk),
    })));
    return results.flat();
  } finally {
    setLoading?.(false);
  }
};

export const addRequest = async (request, setLoading) =>
  addDataToTable({ tableName: 'requests', data: request, setLoading });

export const rejectRequest = async (id, setLoading) =>
  deleteDocById({ tableName: 'requests', id, setLoading });

export const addReport = async (report, setLoading) =>
  addDataToTable({ tableName: 'reports', data: report, setLoading });

export const fetchStatsByUser = async (userId, setLoading) =>
  fetchByTable({
    tableName: 'stats',
    whereCondition: where('userId', '==', userId),
    orderByField: 'date',
    setLoading,
  });
